import React, { useEffect, useState } from 'react';
import {
  Table, Tag, Typography, Select, Button, Alert, Card, Modal, Form, Input,
  message, Drawer, Descriptions, Radio, Divider, Space, DatePicker, Upload,
} from 'antd';
import { PlusOutlined, UploadOutlined, CheckCircleOutlined, WarningOutlined, StopOutlined } from '@ant-design/icons';
import {
  getProjects, getWIRs, getWIR, createWIR, closePunchPoint, escalatePunchPoint, getWorkflowObjects,
} from '../services/api';
import { getApiError } from '../utils/apiError';
import { useNavigation } from '../context/NavigationContext';
import RecordTimeline from '../components/RecordTimeline';
import StagePhotos from '../components/StagePhotos';
import { mediaUrl } from '../utils/media';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Project {
  id: string;
  code: string;
  name: string;
}

interface ChecklistItemDraft {
  item_name: string;
  result: 'PASS' | 'FAIL' | '';
  is_hold_point: boolean;
  remarks: string;
  evidenceFile: File | null;
}

// Media files (photos) are served from the Django backend's /media/ path,
// not the /api/v1 prefix, so strip that off to build a browsable URL.

const emptyChecklistItem = (): ChecklistItemDraft => ({
  item_name: '', result: '', is_hold_point: false, remarks: '', evidenceFile: null,
});

const WIRPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [wirs, setWirs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [checklistItems, setChecklistItems] = useState<ChecklistItemDraft[]>([emptyChecklistItem()]);
  const [generalPhotos, setGeneralPhotos] = useState<File[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedWIR, setSelectedWIR] = useState<any>(null);

  const [actionModal, setActionModal] = useState<{ type: 'close' | 'escalate'; punchPoint: any; wirId: string } | null>(null);
  const [closureRemarks, setClosureRemarks] = useState('');
  const [closureEvidence, setClosureEvidence] = useState<File | null>(null);
  const [escalateTo, setEscalateTo] = useState<string>('');
  const [escalationComments, setEscalationComments] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const { pendingTarget, clearPendingTarget } = useNavigation();
  const target = pendingTarget?.recordType === 'WIR' ? pendingTarget : null;

  // Opens the record that brought the user here through a clickable ID, once the list has loaded.
  useEffect(() => {
    if (!target || loading) return;
    const match = wirs.find((w) => w.workflow?.object_id === target.recordId);
    if (match) {
      openDrawer(match);
      clearPendingTarget();
    }
  }, [wirs, loading, target, clearPendingTarget]);

  // A pending target only belongs to the visit it was set for.
  useEffect(() => clearPendingTarget, [clearPendingTarget]);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        const initial = response.data.find((p: any) => p.id === target?.projectId) || response.data[0];
        setSelectedProject(initial.id);
        loadWIRs(initial.id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadWIRs = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getWIRs(projectId);
      const workflowResponse = await getWorkflowObjects(projectId, { type: 'WIR' });
      const workflowMap: { [key: string]: any } = {};
      workflowResponse.data.forEach((wo: any) => {
        workflowMap[wo.id] = wo;
      });
      const enriched = response.data.map((wir: any) => ({
        ...wir,
        workflow: workflowMap[wir.workflow_object] || {},
      }));
      setWirs(enriched);
    } catch (err) {
      setError('Failed to load WIRs.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadWIRs(value);
  };

  // ---------- Raise WIR modal ----------

  const openModal = () => {
    form.resetFields();
    setChecklistItems([emptyChecklistItem()]);
    setGeneralPhotos([]);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const updateChecklistItem = (index: number, patch: Partial<ChecklistItemDraft>) => {
    const updated = [...checklistItems];
    updated[index] = { ...updated[index], ...patch };
    setChecklistItems(updated);
  };

  const addChecklistItem = () => {
    setChecklistItems([...checklistItems, emptyChecklistItem()]);
  };

  const removeChecklistItem = (index: number) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (checklistItems.length === 0) {
        message.error('Add at least one checklist item.');
        return;
      }
      for (const item of checklistItems) {
        if (!item.item_name.trim() || !item.result) {
          message.error('Every checklist item needs a name and a Pass/Fail result.');
          return;
        }
        if (item.result === 'FAIL' && !item.evidenceFile) {
          message.error(`Evidence photo required for failed item: ${item.item_name}`);
          return;
        }
      }

      setSubmitting(true);

      const checklistPayload = checklistItems.map((item) => ({
        item_name: item.item_name,
        result: item.result,
        is_hold_point: item.result === 'FAIL' ? item.is_hold_point : false,
        remarks: item.remarks || '',
      }));

      const formData = new FormData();
      formData.append('activity_type', values.activity_type);
      formData.append('location_reference', values.location_reference);
      formData.append('inspection_date', values.inspection_date.format('YYYY-MM-DD'));
      formData.append('checklist_results', JSON.stringify(checklistPayload));

      checklistItems.forEach((item, idx) => {
        if (item.result === 'FAIL' && item.evidenceFile) {
          formData.append(`punch_evidence_${idx}`, item.evidenceFile);
        }
      });
      generalPhotos.forEach((file) => {
        formData.append('general_photos', file);
      });

      await createWIR(selectedProject, formData);
      message.success('WIR submitted and finalized.');
      setModalOpen(false);
      loadWIRs(selectedProject);
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      message.error(getApiError(err, 'Failed to submit WIR.'), 8);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Detail drawer ----------

  const openDrawer = async (record: any) => {
    setSelectedWIR(record);
    setDrawerOpen(true);
    try {
      const res = await getWIR(record.id);
      setSelectedWIR({ ...res.data, workflow: record.workflow });
    } catch (err) {
      // Keep the row's data if the refetch fails; non-fatal.
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedWIR(null);
  };

  // Takes an explicit wirId rather than reading selectedWIR from closure,
  // so it can never act on a stale reference regardless of render timing.
  const refreshWIR = async (wirId: string) => {
    try {
      const res = await getWIR(wirId);
      setSelectedWIR((prev: any) => ({ ...res.data, workflow: prev?.workflow }));
      loadWIRs(selectedProject);
    } catch (err) {
      // non-fatal
    }
  };

  // ---------- Punch point close / escalate ----------

  const openActionModal = (type: 'close' | 'escalate', punchPoint: any) => {
    setClosureRemarks('');
    setClosureEvidence(null);
    setEscalateTo('');
    setEscalationComments('');
    setActionModal({ type, punchPoint, wirId: selectedWIR.id });
  };

  const closeActionModal = () => setActionModal(null);

  const handleClosePunchPoint = async () => {
    if (!actionModal) return;
    if (!closureEvidence) {
      message.error('Closure evidence photo is required.');
      return;
    }
    setActionSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('closure_remarks', closureRemarks);
      formData.append('evidence', closureEvidence);
      await closePunchPoint(actionModal.punchPoint.id, formData);
      message.success('Punch point closed.');
      const wirId = actionModal.wirId;
      closeActionModal();
      refreshWIR(wirId);
    } catch (err: any) {
      message.error(getApiError(err, 'Failed to close punch point.'), 8);
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleEscalatePunchPoint = async () => {
    if (!actionModal) return;
    if (!escalateTo) {
      message.error('Select who to escalate to.');
      return;
    }
    setActionSubmitting(true);
    try {
      await escalatePunchPoint(actionModal.punchPoint.id, {
        escalated_to: escalateTo,
        escalation_comments: escalationComments,
      });
      message.success('Punch point escalated.');
      const wirId = actionModal.wirId;
      closeActionModal();
      refreshWIR(wirId);
    } catch (err: any) {
      message.error(getApiError(err, 'Failed to escalate punch point.'), 8);
    } finally {
      setActionSubmitting(false);
    }
  };

  // ---------- Rendering helpers ----------

  const resultTag = (result: string) => {
    if (result === 'PASS') return <Tag icon={<CheckCircleOutlined />} color="green">Pass</Tag>;
    if (result === 'CLEARED_WITH_SNAG') return <Tag icon={<WarningOutlined />} color="gold">Cleared with Snag</Tag>;
    if (result === 'BLOCKED') return <Tag icon={<StopOutlined />} color="red">Blocked — Hold Point</Tag>;
    return <Tag>{result}</Tag>;
  };

  const punchStatusTag = (status: string) => {
    if (status === 'OPEN') return <Tag color="orange">Open</Tag>;
    if (status === 'ESCALATED') return <Tag color="volcano">Escalated</Tag>;
    if (status === 'CLOSED') return <Tag color="green">Closed</Tag>;
    return <Tag>{status}</Tag>;
  };

  const columns = [
    {
      title: 'WIR ID',
      dataIndex: ['workflow', 'object_id'],
      key: 'object_id',
      render: (text: string) => <Text strong style={{ color: '#1B2A4A' }}>{text}</Text>,
    },
    { title: 'Activity', dataIndex: 'activity_type', key: 'activity_type' },
    { title: 'Location', dataIndex: 'location_reference', key: 'location_reference' },
    {
      title: 'Result',
      dataIndex: 'overall_result',
      key: 'overall_result',
      render: (result: string) => resultTag(result),
    },
    {
      title: 'Open Punch Points',
      key: 'open_punch_points',
      render: (_: any, record: any) => {
        const open = (record.punch_points || []).filter((pp: any) => pp.status !== 'CLOSED').length;
        return open > 0 ? <Tag color="red">{open} open</Tag> : <Tag color="green">All closed</Tag>;
      },
    },
    {
      title: 'Inspection Date',
      dataIndex: 'inspection_date',
      key: 'inspection_date',
      render: (date: string) => new Date(date).toLocaleDateString('en-IN'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          WIR Register
        </Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 250 }}
            options={projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }} onClick={openModal} disabled={!selectedProject}>
            Raise WIR
          </Button>
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={wirs}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No WIRs found for this project.' }}
          onRow={(record) => ({
            onClick: () => openDrawer(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* ---------- Detail Drawer ---------- */}
      <Drawer
        title={selectedWIR?.workflow?.object_id || 'WIR Details'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={620}
      >
        {selectedWIR && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Activity Type">{selectedWIR.activity_type}</Descriptions.Item>
              <Descriptions.Item label="Location">{selectedWIR.location_reference}</Descriptions.Item>
              <Descriptions.Item label="Inspection Date">
                {new Date(selectedWIR.inspection_date).toLocaleDateString('en-IN')}
              </Descriptions.Item>
              <Descriptions.Item label="Result">{resultTag(selectedWIR.overall_result)}</Descriptions.Item>
            </Descriptions>

            <Divider>Checklist</Divider>
            {(selectedWIR.checklist_results || []).map((item: any, index: number) => (
              <Card key={index} size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>{item.item_name}</Text>
                  {item.result === 'PASS' ? (
                    <Tag color="green">Pass</Tag>
                  ) : (
                    <Tag color={item.is_hold_point ? 'red' : 'gold'}>
                      {item.is_hold_point ? 'Fail — Hold Point' : 'Fail — Snag'}
                    </Tag>
                  )}
                </div>
                {item.remarks && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{item.remarks}</div>}
              </Card>
            ))}

            <Divider>Photos</Divider>
            <StagePhotos attachments={selectedWIR.attachments} />

            <Divider>Punch Points</Divider>
            {(selectedWIR.punch_points || []).length === 0 ? (
              <Text type="secondary">No punch points — all checklist items passed.</Text>
            ) : (
              (selectedWIR.punch_points || []).map((pp: any) => (
                <Card key={pp.id} size="small" style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Text strong>{pp.display_id} — {pp.item_name}</Text>
                    {punchStatusTag(pp.status)}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{pp.description}</div>

                  {(pp.attachments || []).length > 0 && (
                    <Space style={{ marginBottom: 8 }}>
                      {pp.attachments.map((att: any) => (
                        <a key={att.id} href={mediaUrl(att.file)} target="_blank" rel="noopener noreferrer">
                          <img src={mediaUrl(att.file)} alt={att.caption} title={att.caption} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                        </a>
                      ))}
                    </Space>
                  )}

                  {pp.status === 'CLOSED' ? (
                    <div style={{ fontSize: 12 }}>
                      <Text type="secondary">Closed — {pp.closure_remarks}</Text>
                    </div>
                  ) : pp.status === 'ESCALATED' ? (
                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                      <Text type="secondary">Escalated to {pp.escalated_to} — {pp.escalation_comments}</Text>
                    </div>
                  ) : null}

                  {pp.status !== 'CLOSED' && (
                    <Space>
                      <Button size="small" type="primary" style={{ background: '#1B2A4A' }} onClick={() => openActionModal('close', pp)}>
                        Close
                      </Button>
                      <Button size="small" onClick={() => openActionModal('escalate', pp)}>
                        Escalate
                      </Button>
                    </Space>
                  )}
                </Card>
              ))
            )}

            <Divider>Timeline</Divider>
            <RecordTimeline recordId={selectedWIR.workflow?.object_id} projectId={selectedProject} />
          </>
        )}
      </Drawer>

      {/* ---------- Raise WIR Modal ---------- */}
      <Modal
        title="Raise WIR"
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Submit & Finalize"
        width={680}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="activity_type" label="Activity Type" style={{ flex: 1 }} rules={[{ required: true, message: 'Activity type is required' }]}>
              <Input placeholder="e.g. Reinforcement, Formwork, Concreting" />
            </Form.Item>
            <Form.Item name="location_reference" label="Location" style={{ flex: 1 }} rules={[{ required: true, message: 'Location is required' }]}>
              <Input placeholder="e.g. Column C-12, Level 3" />
            </Form.Item>
          </div>
          <Form.Item name="inspection_date" label="Inspection Date" rules={[{ required: true, message: 'Inspection date is required' }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
        </Form>

        <Divider>Checklist</Divider>
        {checklistItems.map((item, index) => (
          <Card key={index} size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Input
                placeholder="Checklist item name"
                value={item.item_name}
                onChange={(e) => updateChecklistItem(index, { item_name: e.target.value })}
              />
              {checklistItems.length > 1 && (
                <Button danger onClick={() => removeChecklistItem(index)}>Remove</Button>
              )}
            </div>
            <Radio.Group
              value={item.result}
              onChange={(e) => updateChecklistItem(index, { result: e.target.value })}
              style={{ marginBottom: 8 }}
            >
              <Radio value="PASS">Pass</Radio>
              <Radio value="FAIL">Fail</Radio>
            </Radio.Group>

            {item.result === 'FAIL' && (
              <>
                <div style={{ marginBottom: 8 }}>
                  <Text>Judgment call: </Text>
                  <Radio.Group
                    value={item.is_hold_point}
                    onChange={(e) => updateChecklistItem(index, { is_hold_point: e.target.value })}
                  >
                    <Radio value={true}>Hold Point (blocks work)</Radio>
                    <Radio value={false}>Snag-clearable</Radio>
                  </Radio.Group>
                </div>
                <TextArea
                  placeholder="Remarks (what's wrong)"
                  value={item.remarks}
                  onChange={(e) => updateChecklistItem(index, { remarks: e.target.value })}
                  style={{ marginBottom: 8 }}
                  rows={2}
                />
                <Upload
                  beforeUpload={(file) => { updateChecklistItem(index, { evidenceFile: file }); return false; }}
                  onRemove={() => updateChecklistItem(index, { evidenceFile: null })}
                  maxCount={1}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />}>
                    {item.evidenceFile ? item.evidenceFile.name : 'Upload Evidence Photo (required)'}
                  </Button>
                </Upload>
              </>
            )}
          </Card>
        ))}
        <Button type="dashed" block onClick={addChecklistItem} style={{ marginBottom: 16 }}>
          + Add Checklist Item
        </Button>

        <Divider>General Site Photos (optional)</Divider>
        <Upload
          beforeUpload={(file) => { setGeneralPhotos((prev) => [...prev, file]); return false; }}
          onRemove={(file) => setGeneralPhotos((prev) => prev.filter((f) => f.name !== file.name))}
          multiple
          accept="image/*"
        >
          <Button icon={<UploadOutlined />}>Upload Proof-of-Visit Photos</Button>
        </Upload>
      </Modal>

      {/* ---------- Close / Escalate Action Modal ---------- */}
      <Modal
        title={actionModal?.type === 'close' ? 'Close Punch Point' : 'Escalate Punch Point'}
        open={!!actionModal}
        onCancel={closeActionModal}
        onOk={actionModal?.type === 'close' ? handleClosePunchPoint : handleEscalatePunchPoint}
        confirmLoading={actionSubmitting}
        okText={actionModal?.type === 'close' ? 'Close' : 'Escalate'}
      >
        {actionModal?.type === 'close' ? (
          <>
            <TextArea
              placeholder="Closure remarks"
              value={closureRemarks}
              onChange={(e) => setClosureRemarks(e.target.value)}
              rows={3}
              style={{ marginBottom: 12 }}
            />
            <Upload
              beforeUpload={(file) => { setClosureEvidence(file); return false; }}
              onRemove={() => setClosureEvidence(null)}
              maxCount={1}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>
                {closureEvidence ? closureEvidence.name : 'Upload Closure Evidence (required)'}
              </Button>
            </Upload>
          </>
        ) : (
          <>
            <Form.Item label="Escalate To" style={{ marginBottom: 12 }}>
              <Select
                placeholder="Select target"
                value={escalateTo || undefined}
                onChange={setEscalateTo}
                options={[
                  { value: 'PMC', label: 'PMC' },
                  { value: 'CONTRACTOR', label: 'Contractor' },
                  { value: 'CLIENT', label: 'Client' },
                ]}
              />
            </Form.Item>
            <TextArea
              placeholder="Escalation comments"
              value={escalationComments}
              onChange={(e) => setEscalationComments(e.target.value)}
              rows={3}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default WIRPage;